import React from 'react';
import { TextV2 } from '@withjoy/joykit';
import { dottedUnderlineStyles, StyledStandaloneTextButton } from './DottedButton.styles';

type DottedTextButtonProps = Readonly<{
  onClick?: () => void;
  isDisabled?: boolean;
  typographyVariant?: React.ComponentProps<typeof TextV2>['typographyVariant'];
  /**
   * A text action on a line of its own, rather than inside a sentence: it keeps its own type and color
   * and gets a 44px tall tap target around the label.
   */
  standalone?: boolean;
  /** The label color of a standalone action; defaults to mono12. A themed surface passes its own text color. */
  color?: React.ComponentProps<typeof TextV2>['color'];
  children?: React.ReactNode;
}>;

export const DottedTextButton: React.FC<DottedTextButtonProps> = ({ onClick, isDisabled, typographyVariant = 'body2', standalone = false, color = 'mono12', children }) => {
  const interaction = {
    type: 'button' as const,
    onClick,
    disabled: isDisabled,
    cursor: isDisabled ? 'default' : 'pointer',
    _hover: isDisabled ? undefined : { color: 'buttonPrimaryHover' },
    _focusVisible: { color: 'buttonPrimaryHover', outline: '2px solid', outlineColor: 'newUIBrandPrimary', outlineOffset: '2px' },
  };

  if (standalone) {
    return (
      <StyledStandaloneTextButton
        forwardedAs="button"
        display="inline-flex"
        alignItems="center"
        minHeight="2.75rem"
        typographyVariant={typographyVariant}
        fontWeight={600}
        color={color}
        backgroundColor="transparent"
        border="none"
        padding={0}
        {...interaction}
      >
        {children}
      </StyledStandaloneTextButton>
    );
  }

  return (
    <TextV2
      as="button"
      display="inline"
      typographyVariant={typographyVariant}
      fontWeight={600}
      color="mono12"
      backgroundColor="transparent"
      border="none"
      padding={0}
      css={dottedUnderlineStyles}
      {...interaction}
    >
      {children}
    </TextV2>
  );
};
